import { useIntl } from "react-intl";
import { SimpleEntityGridColumn } from "../grid/SimpleEntityGrid";
import { KTSVG } from "../../../_metronic/helpers";
import { AdminStat } from "src/app/types/orders/order";
import { round } from "lodash";

export default function useFillingGridsDashboard(): Array<SimpleEntityGridColumn> {
    const { formatMessage } = useIntl();

    const cellRenderer = ({ data }: { data: AdminStat }) => {
        return (
            <div className="d-flex">
                <a className="inline-action" href={`https://${data.domain}`} target="_blank">
                    <KTSVG path="/media/icons/duotune/general/gen004.svg" className="svg-icon-2" />
                </a>
                <a
                    className="inline-action"
                    href={`/analyse/zoom/content?moneySite=${data.id}`}
                    target="_blank"
                >
                    <KTSVG path="/media/icons/duotune/graphs/gra004.svg" className="svg-icon-2" />
                </a>
            </div>
        );
    };

    const gridColumns: Array<SimpleEntityGridColumn> = [
        {
            field: "actions",
            sortable: false,
            filter: false,
            pinned: "left",
            cellRenderer,
            cellRendererParams: ({ data }: { data: AdminStat }) => ({ data }),
            minWidth: 85,
            maxWidth: 85,
        },
        {
            field: "domain",
            headerName: formatMessage({ id: "sites.grid.col.domain" }),
            pinned: "left",
        },
        {
            field: "pbn",
            headerName: formatMessage({ id: "sites.grid.col.pbn" }),
            pinned: "left",
            minWidth: 80,
            maxWidth: 80,
        },
        {
            field: "planned",
            headerName: formatMessage({ id: "sites.grid.col.planned" }),
            valueGetter: ({ data }: { data: AdminStat }) => data?.planned || 0,
            cellClass: "number-cell",
            minWidth: 90,
            maxWidth: 90,
        },
        {
            headerName: formatMessage({ id: "sites.grid.col.previousMonth" }),
            children: [
                {
                    field: "previousMonthOrdered",
                    headerName: formatMessage({ id: "sites.grid.col.ordered" }),
                    valueGetter: ({ data }: { data: AdminStat }) => data?.previousMonthOrdered || 0,
                    cellStyle: { borderLeft: "1px solid var(--ag-border-color)" },
                    cellClass: "number-cell",
                    cellClassRules: {
                        "order-success": ({ data }: { data: AdminStat }) =>
                            data.previousMonthOrdered >= data.planned - 2,
                    },
                    minWidth: 90,
                    maxWidth: 90,
                },
                {
                    field: "previousMonthPublished",
                    headerName: formatMessage({ id: "sites.grid.col.published" }),
                    cellClass: "number-cell",
                    cellClassRules: {
                        "order-success": ({ data }: { data: AdminStat }) =>
                            data.previousMonthPublished >= data.previousMonthOrdered &&
                            data.previousMonthPublished >= data.planned - 2,
                    },
                    valueGetter: ({ data }: { data: AdminStat }) =>
                        data?.previousMonthPublished || 0,
                    minWidth: 90,
                    maxWidth: 90,
                },
                {
                    field: "previousMonthIndexed",
                    headerName: formatMessage({ id: "sites.grid.col.indexed" }),
                    cellClass: "number-cell",
                    cellClassRules: {
                        "order-success": ({ data }: { data: AdminStat }) =>
                            data.previousMonthIndexed >= data.previousMonthPublished &&
                            data.previousMonthPublished > 0,
                    },
                    valueGetter: ({ data }: { data: AdminStat }) =>
                        round(
                            ((data?.previousMonthIndexed || 0) * 100) /
                                (data?.previousMonthOrdered || 1)
                        ),
                    valueFormatter: ({ data }: { data: AdminStat }) =>
                        `${round(
                            ((data?.previousMonthIndexed || 0) * 100) /
                                (data?.previousMonthOrdered || 1)
                        )}%`,
                    minWidth: 90,
                    maxWidth: 90,
                },
            ],
        },
        {
            headerName: formatMessage({ id: "sites.grid.col.currentMonth" }),
            children: [
                {
                    field: "thisMonthOrdered",
                    headerName: formatMessage({ id: "sites.grid.col.ordered" }),
                    cellClass: "number-cell",
                    valueGetter: ({ data }: { data: AdminStat }) => data?.thisMonthOrdered || 0,
                    cellStyle: { borderLeft: "1px solid var(--ag-border-color)" },
                    cellClassRules: {
                        "order-success": ({ data }: { data: AdminStat }) =>
                            data.thisMonthOrdered >= data.planned - 2,
                    },
                    minWidth: 90,
                    maxWidth: 90,
                },
                {
                    field: "thisMonthPublished",
                    headerName: formatMessage({ id: "sites.grid.col.published" }),
                    cellClass: "number-cell",
                    cellClassRules: {
                        "order-success": ({ data }: { data: AdminStat }) =>
                            data.thisMonthPublished >= data.thisMonthOrdered &&
                            data.thisMonthPublished >= data.planned - 2,
                    },
                    valueGetter: ({ data }: { data: AdminStat }) => data?.thisMonthPublished || 0,
                    minWidth: 90,
                    maxWidth: 90,
                },
                {
                    field: "thisMonthIndexed",
                    headerName: formatMessage({ id: "sites.grid.col.indexed" }),
                    cellClass: "number-cell",
                    cellClassRules: {
                        "order-success": ({ data }: { data: AdminStat }) =>
                            data.thisMonthIndexed >= data.thisMonthPublished &&
                            data.thisMonthPublished > 0,
                    },
                    valueGetter: ({ data }: { data: AdminStat }) =>
                        round(
                            ((data?.thisMonthIndexed || 0) * 100) / (data?.thisMonthOrdered || 1)
                        ),
                    valueFormatter: ({ data }: { data: AdminStat }) =>
                        `${round(
                            ((data?.thisMonthIndexed || 0) * 100) / (data?.thisMonthOrdered || 1)
                        )}%`,
                    minWidth: 90,
                    maxWidth: 90,
                },
                {
                    field: "thisMonthLate",
                    headerName: formatMessage({ id: "sites.grid.col.latePublished" }),
                    cellClass: "number-cell",
                    cellClassRules: {
                        "order-fail": ({ data }: { data: AdminStat }) => data.thisMonthLate > 0,
                    },
                    valueGetter: ({ data }: { data: AdminStat }) => data?.thisMonthLate || 0,
                    cellStyle: { borderRight: "1px solid var(--ag-border-color)" },

                    minWidth: 90,
                    maxWidth: 90,
                },
            ],
        },
    ];

    return gridColumns;
}
